import { useEffect, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile widget (ARCHITECTURE_PLAN §1.1 bot defense, §6.1 login
 * flow). Loads the real Turnstile JS API from Cloudflare and renders the
 * challenge explicitly into this component's container. The produced token is
 * sent to the Worker, which verifies it server-side (`src/auth/turnstile.ts`).
 *
 * The site key is public client configuration supplied at build time as the
 * `VITE_TURNSTILE_SITE_KEY` environment variable (Turnstile site keys are
 * designed to be embedded in client-side code). A missing key is a loud
 * configuration error rendered in place of the widget — never a silently
 * skipped bot check.
 *
 * Failure model (no silent fallbacks):
 *   - script load failure → visible error state, no token is ever produced;
 *   - challenge error/expiry → the token is cleared (`onToken(null)`) so the
 *     surrounding form re-locks until a fresh challenge completes.
 */

/** Subset of the Turnstile JS API this app uses (explicit render mode). */
export interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: (errorCode: string) => void;
  'expired-callback'?: () => void;
}

export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Singleton script loader. If `window.turnstile` already exists (script
 * previously loaded) it resolves immediately without injecting a duplicate
 * tag. A rejected load resets the singleton so a remount can retry.
 */
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstileApi(): Promise<TurnstileApi> {
  if (window.turnstile !== undefined) {
    return Promise.resolve(window.turnstile);
  }
  if (scriptPromise !== null) {
    return scriptPromise;
  }
  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile !== undefined) {
        resolve(window.turnstile);
      } else {
        scriptPromise = null;
        reject(new Error('the Turnstile script loaded but did not initialize'));
      }
    };
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('the Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface TurnstileWidgetProps {
  /** Public Turnstile site key (build-time `VITE_TURNSTILE_SITE_KEY`). */
  siteKey: string;
  /** Called with the token on success, and with `null` on expiry/error. */
  onToken: (token: string | null) => void;
}

export function TurnstileWidget({ siteKey, onToken }: TurnstileWidgetProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The latest callback is read through a ref so the widget is rendered once
  // per site key instead of re-rendering on every parent render.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (siteKey === '') {
      setLoadError(
        'Bot verification is not configured (VITE_TURNSTILE_SITE_KEY is missing at build time).'
      );
      return;
    }

    let cancelled = false;
    let widgetId: string | null = null;

    loadTurnstileApi()
      .then((api) => {
        if (cancelled) {
          return;
        }
        const container = containerRef.current;
        if (container === null) {
          setLoadError('Bot verification failed to initialize. Reload the page.');
          return;
        }
        widgetId = api.render(container, {
          sitekey: siteKey,
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
        setReady(true);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Bot verification failed to load.');
        }
      });

    return () => {
      cancelled = true;
      if (widgetId !== null && window.turnstile !== undefined) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [siteKey]);

  if (loadError !== null) {
    return (
      <p
        role="alert"
        className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
      >
        Bot verification unavailable: {loadError}
      </p>
    );
  }

  return (
    <div>
      <div ref={containerRef} data-testid="turnstile-widget" />
      {!ready && (
        <p role="status" className="text-sm text-slate-500">
          Loading verification…
        </p>
      )}
    </div>
  );
}
